import React from "react";

import type {
  ListingInferenceReviewPortalDecisionDraft,
  ListingInferenceReviewPortalWorkflowProps,
} from "@view/listing/inference/reviewPortal/ListingInferenceReviewPortalPhase";

const dispositions = ["all", "proposed", "confirmed", "rejected", "conflicted"] as const;
const claimKinds = ["all", "partyIdentity", "partyRole", "partyRelationship"] as const;
const decisionTargets = ["confirmed", "rejected", "conflicted"] as const;

const updateDraft = (
  draft: ListingInferenceReviewPortalDecisionDraft,
  patch: Partial<ListingInferenceReviewPortalDecisionDraft>,
): ListingInferenceReviewPortalDecisionDraft => ({
  ...draft,
  ...patch,
});

const ListingInferenceReviewPortalWorkflow: React.FC<ListingInferenceReviewPortalWorkflowProps> = ({
  phase,
  listingId,
  hasProvider,
  hasProgramBoundary,
  reviewQueue,
  directListingId,
  claims,
  gaps,
  summary,
  decisionTargetMessage,
  selectedClaimContextMessage,
  errorMessages,
  acceptedDecisionMessages,
  provenanceRows,
  provenanceEmptyMessage,
  filterDisposition,
  filterKind,
  includeGaps,
  readOnly,
  draft,
  onDirectListingIdChange,
  onLoadDirectListing,
  onSelectListing,
  onFilterDispositionChange,
  onFilterKindChange,
  onIncludeGapsChange,
  onSelectClaim,
  onNextReview,
  onDraftChange,
  onRetryLoad,
  onSubmitDecision,
  onRequestUnsafeFactPromotion,
}) => {
  const providerStatus = hasProvider ? "mounted" : "missing";
  const programStatus = hasProgramBoundary ? "available" : "missing";

  return (
    <section aria-label="Listing inference review portal" data-testid="listing-inference-review-portal-workflow">
      <header>
        <h1>Listing inference review</h1>
        <p data-testid="listing-inference-review-portal-phase">{phase}</p>
        <dl>
          <dt>Listing</dt>
          <dd data-testid="listing-inference-review-portal-listing">{listingId}</dd>
          <dt>Provider</dt>
          <dd data-testid="listing-inference-review-portal-provider">{providerStatus}</dd>
          <dt>Program</dt>
          <dd data-testid="listing-inference-review-portal-program">{programStatus}</dd>
        </dl>
      </header>

    <section data-testid="listing-inference-review-portal-selector">
      <h2>Review queue</h2>
      <p hidden={reviewQueue.length > 0} data-testid="listing-inference-review-portal-empty-queue">
        No queued listing reviews are available. Enter a listing id to load a workspace directly.
      </p>
      <ul hidden={reviewQueue.length === 0}>
        {reviewQueue.map((item) => (
          <li key={item.listingId}>
            <button type="button" onClick={() => onSelectListing(item.listingId)}>
              {item.label}
            </button>
            <span> {item.nextReview}</span>
          </li>
        ))}
      </ul>
      <form
        aria-label="Direct listing selection"
        onSubmit={(event) => {
          event.preventDefault();
          onLoadDirectListing();
        }}
      >
        <label>
          Listing id
          <input value={directListingId} onChange={(event) => onDirectListingIdChange(event.target.value)} />
        </label>
        <button type="submit">Load listing</button>
      </form>
    </section>

    {errorMessages.map((message) => (
      <div key={message.id} role="alert" data-testid="listing-inference-review-portal-error">
        <p>{message.message}</p>
        <button type="button" onClick={onRetryLoad}>
          Retry
        </button>
      </div>
    ))}

    {acceptedDecisionMessages.map((message) => (
      <p key={message.id} data-testid="listing-inference-review-portal-accepted">{message.message}</p>
    ))}

    <div data-testid="listing-inference-review-portal-summary">
      <span>Proposed {summary.proposed}</span>
      <span>Confirmed {summary.confirmed}</span>
      <span>Rejected {summary.rejected}</span>
      <span>Conflicted {summary.conflicted}</span>
    </div>

    <fieldset>
      <legend>Filters</legend>
      <label>
        Claim kind
        <select value={filterKind} onChange={(event) => onFilterKindChange(event.target.value)}>
          {claimKinds.map((kind) => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
        </select>
      </label>
      <label>
        Disposition
        <select value={filterDisposition} onChange={(event) => onFilterDispositionChange(event.target.value)}>
          {dispositions.map((disposition) => (
            <option key={disposition} value={disposition}>
              {disposition}
            </option>
          ))}
        </select>
      </label>
      <label>
        <input
          checked={includeGaps}
          onChange={(event) => onIncludeGapsChange(event.target.checked)}
          type="checkbox"
        />
        Show gaps
      </label>
    </fieldset>

    <table data-testid="listing-inference-review-portal-claims">
      <thead>
        <tr>
          <th>Claim</th>
          <th>Kind</th>
          <th>Disposition</th>
          <th>Evidence</th>
        </tr>
      </thead>
      <tbody>
        {claims.map((claim) => (
          <tr key={claim.id}>
            <td>
              <button
                type="button"
                aria-current={claim.id === draft.claimId}
                onClick={() => onSelectClaim(claim.id)}
              >
                {claim.value}
              </button>
            </td>
            <td>{claim.kind}</td>
            <td>{claim.disposition}</td>
            <td>{claim.nonFactStatus}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p hidden={claims.length > 0} data-testid="listing-inference-review-portal-empty-claims">
      No claims match the current listing and filters.
    </p>

    <section data-testid="listing-inference-review-portal-provenance">
      <h2>Provenance</h2>
      <p>{provenanceEmptyMessage}</p>
      <dl>
        {provenanceRows.map((row) => (
          <React.Fragment key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>

    <section data-testid="listing-inference-review-portal-gaps">
      <h2>Gaps</h2>
      <ul>
        {gaps.map((gap) => (
          <li key={`${gap.sourceRef}-${gap.question}`}>
            {gap.question} ({gap.status})
          </li>
        ))}
      </ul>
    </section>

    <form
      data-testid="listing-inference-review-portal-decision-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmitDecision();
      }}
    >
      <p data-testid="listing-inference-review-portal-decision-target">{decisionTargetMessage}</p>
      <p data-testid="listing-inference-review-portal-selected-claim">{selectedClaimContextMessage}</p>
      <label>
        Target
        <select
          disabled={readOnly}
          value={draft.targetDisposition}
          onChange={(event) => onDraftChange(updateDraft(draft, { targetDisposition: event.target.value }))}
        >
          {decisionTargets.map((target) => (
            <option key={target} value={target}>
              {target}
            </option>
          ))}
        </select>
      </label>
      <label>
        Reviewer
        <input
          disabled={readOnly}
          value={draft.reviewer}
          onChange={(event) => onDraftChange(updateDraft(draft, { reviewer: event.target.value }))}
        />
      </label>
      <label>
        Reason
        <textarea
          disabled={readOnly}
          value={draft.reason}
          onChange={(event) => onDraftChange(updateDraft(draft, { reason: event.target.value }))}
        />
      </label>
      <button disabled={readOnly} type="submit">
        Submit decision
      </button>
      <button disabled={claims.length === 0} type="button" onClick={onNextReview}>
        Next review
      </button>
    </form>

    <button type="button" onClick={onRequestUnsafeFactPromotion}>
      Test unsafe fact promotion refusal
    </button>
    </section>
  );
};

export default ListingInferenceReviewPortalWorkflow;
